<template>
  <div class="nr">
    <div class="nr__head">
      <strong>Network Radar</strong>
      <span class="nr__meta">{{ onlineCount }}/{{ points.length }} online</span>
    </div>

    <div v-if="!points.length" class="nr__empty">No devices</div>
    <div v-else class="nr__body">
      <svg class="nr__radar" :viewBox="`0 0 ${SIZE} ${SIZE}`">
        <defs>
          <radialGradient id="nr-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="rgba(34,198,171,0.25)" />
            <stop offset="70%" stop-color="rgba(71,152,232,0.08)" />
            <stop offset="100%" stop-color="rgba(0,0,0,0)" />
          </radialGradient>
        </defs>
        <circle :cx="CX" :cy="CY" :r="R" fill="url(#nr-glow)" />
        <circle
          v-for="ring in rings"
          :key="'r' + ring"
          :cx="CX"
          :cy="CY"
          :r="ring"
          class="nr__ring"
        />
        <line
          v-for="a in axes"
          :key="'a' + a"
          :x1="CX"
          :y1="CY"
          :x2="CX + Math.cos(a) * R"
          :y2="CY + Math.sin(a) * R"
          class="nr__axis"
        />
        <polygon
          v-if="upPoly"
          class="nr__poly nr__poly--up"
          :points="upPoly"
        />
        <polygon
          v-if="downPoly"
          class="nr__poly nr__poly--down"
          :points="downPoly"
        />
        <g v-for="p in plotted" :key="p.id">
          <circle
            :cx="p.x"
            :cy="p.y"
            :r="p.online ? 5.5 : 4"
            :class="['nr__dot', p.online ? 'is-on' : 'is-off']"
            :style="{ fill: p.color }"
          />
          <text
            :x="p.labelX"
            :y="p.labelY"
            class="nr__label"
            text-anchor="middle"
          >{{ p.short }}</text>
        </g>
        <circle :cx="CX" :cy="CY" r="3" class="nr__hub" />
      </svg>

      <div class="nr__legend">
        <span><i class="nr__swatch nr__swatch--up"></i> Uplink</span>
        <span><i class="nr__swatch nr__swatch--down"></i> Downlink</span>
      </div>

      <div class="nr__list">
        <div v-for="p in points" :key="p.id" class="nr__row">
          <span class="nr__row-name">
            <i class="fas fa-circle" :style="{ color: p.color }"></i>
            {{ p.name }}
          </span>
          <span class="nr__row-rates">
            <span class="is-up">↑ {{ fmtRate(p.up) }}</span>
            <span class="is-down">↓ {{ fmtRate(p.down) }}</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

const SIZE = 200
const CX = 100
const CY = 100
const R = 78
const COLORS = ['#22c6ab', '#4798e8', '#ab47bc', '#ffbc34', '#ef6e6e', '#26c6da', '#7460ee']

function fmtRate(bps) {
  const n = Number(bps)
  if (!Number.isFinite(n) || n < 0) return '—'
  if (n < 1024) return `${n.toFixed(0)}B/s`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}K/s`
  return `${(n / (1024 * 1024)).toFixed(1)}M/s`
}

function parseStats(raw) {
  if (!raw) return null
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw
  } catch (e) {
    return null
  }
}

export default {
  props: {
    config: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      SIZE,
      CX,
      CY,
      R,
      tick: 0,
      timer: null,
    }
  },
  computed: {
    ...mapGetters('mission', ['nodes']),
    displayNodes() {
      const ids = this.config.nodeIds || []
      if (ids.length) return this.nodes.filter((n) => ids.includes(n.id))
      return this.nodes
    },
    points() {
      return this.displayNodes.map((n, i) => {
        const s = parseStats(n.system_stats) || {}
        return {
          id: n.id,
          name: n.name,
          short: (n.name || '?').slice(0, 8),
          online: !!n.online,
          up: Number(s.netUplinkBps) || 0,
          down: Number(s.netDownlinkBps) || 0,
          color: COLORS[i % COLORS.length],
        }
      })
    },
    onlineCount() {
      return this.points.filter((p) => p.online).length
    },
    maxRate() {
      const vals = this.points.flatMap((p) => [p.up, p.down])
      const m = Math.max(1024, ...vals)
      return m * 1.15
    },
    rings() {
      return [R * 0.33, R * 0.66, R]
    },
    axes() {
      const n = Math.max(3, this.points.length)
      return Array.from({ length: n }, (_, i) => (-Math.PI / 2) + (i * 2 * Math.PI) / n)
    },
    plotted() {
      const n = Math.max(1, this.points.length)
      return this.points.map((p, i) => {
        const angle = (-Math.PI / 2) + (i * 2 * Math.PI) / n
        const mag = p.online ? 0.82 : 0.45
        const x = CX + Math.cos(angle) * R * mag
        const y = CY + Math.sin(angle) * R * mag
        return {
          ...p,
          x,
          y,
          labelX: CX + Math.cos(angle) * (R + 14),
          labelY: CY + Math.sin(angle) * (R + 14) + 3,
          angle,
        }
      })
    },
    upPoly() {
      return this.polyFor((p) => p.up)
    },
    downPoly() {
      return this.polyFor((p) => p.down)
    },
  },
  mounted() {
    // soft redraw so radar feels alive even when rates stay flat briefly
    this.timer = setInterval(() => { this.tick += 1 }, 1000)
  },
  beforeDestroy() {
    clearInterval(this.timer)
  },
  methods: {
    fmtRate,
    polyFor(getter) {
      if (!this.plotted.length) return ''
      const n = this.plotted.length
      const pts = this.plotted.map((p, i) => {
        const angle = (-Math.PI / 2) + (i * 2 * Math.PI) / Math.max(1, n)
        const ratio = Math.max(0.08, Math.min(1, getter(p) / this.maxRate))
        const r = R * ratio
        // reference tick so template re-evaluates
        void this.tick
        return `${CX + Math.cos(angle) * r},${CY + Math.sin(angle) * r}`
      })
      return pts.join(' ')
    },
  },
}
</script>

<style scoped>
.nr {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  color: var(--ws-text);
  min-height: 0;
}
.nr__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8em;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.nr__meta {
  opacity: 0.65;
  font-weight: 600;
}
.nr__empty {
  font-size: 0.85em;
  color: var(--ws-text-muted, #6c757d);
}
.nr__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35em;
}
.nr__radar {
  width: 100%;
  max-height: calc(var(--ws-display-size, 32px) * 6.5);
  display: block;
  margin: 0 auto;
}
.nr__ring, .nr__axis {
  fill: none;
  stroke: var(--ws-panel-border, rgba(128, 128, 128, 0.35));
  stroke-width: 1;
  opacity: 0.85;
}
.nr__poly {
  fill-opacity: 0.22;
  stroke-width: 1.75;
  stroke-linejoin: round;
  transition: all 0.6s ease;
}
.nr__poly--up {
  fill: #26c6da;
  stroke: #26c6da;
}
.nr__poly--down {
  fill: #ab47bc;
  stroke: #ab47bc;
}
.nr__dot.is-on {
  stroke: var(--ws-widget-bg, #fff);
  stroke-width: 1.5;
}
.nr__dot.is-off {
  opacity: 0.45;
}
.nr__hub {
  fill: var(--ws-text);
  opacity: 0.55;
}
.nr__label {
  font-size: calc(var(--ws-font-size, 14px) * 0.55);
  fill: var(--ws-text);
  opacity: 0.85;
}
.nr__legend {
  display: flex;
  gap: 0.85em;
  justify-content: center;
  font-size: 0.75em;
  color: var(--ws-text-muted);
}
.nr__swatch {
  display: inline-block;
  width: 0.55em;
  height: 0.55em;
  border-radius: 2px;
  margin-right: 0.25em;
}
.nr__swatch--up { background: #26c6da; }
.nr__swatch--down { background: #ab47bc; }
.nr__list {
  display: flex;
  flex-direction: column;
  gap: 0.25em;
  overflow: auto;
  min-height: 0;
}
.nr__row {
  display: flex;
  justify-content: space-between;
  gap: 0.5em;
  font-size: 0.85em;
  color: var(--ws-text);
}
.nr__row-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
}
.nr__row-name i {
  font-size: 0.4em;
  margin-right: 0.3em;
  vertical-align: middle;
}
.nr__row-rates {
  display: flex;
  gap: 0.55em;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}
.nr__row-rates .is-up { color: #26c6da; }
.nr__row-rates .is-down { color: #ce93d8; }
</style>
